import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Star, Loader2, Check, Trash2, ShieldCheck, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "oz_admin_token";

const Stars = ({ n }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`w-4 h-4 ${i < n ? "fill-[var(--oz-gold)] text-[var(--oz-gold)]" : "text-[var(--oz-border)]"}`} />
    ))}
  </div>
);

export default function AdminReviews() {
  const [token, setToken] = useState(() => localStorage.getItem(LS_KEY) || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("pending");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const load = useCallback(async (tk, status) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/reviews?status=${status}`, {
        headers: { "X-Admin-Token": tk },
      });
      setReviews(data);
      setAuthed(true);
      localStorage.setItem(LS_KEY, tk);
    } catch (e) {
      if (e.response?.status === 401) {
        setError("Token administrateur invalide.");
        setAuthed(false);
        localStorage.removeItem(LS_KEY);
      } else {
        setError("Erreur de chargement.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token, tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const connect = (e) => {
    e.preventDefault();
    setToken(input.trim());
    load(input.trim(), tab);
  };

  const approve = async (id) => {
    await axios.post(`${API}/admin/reviews/${id}/approve`, {}, { headers: { "X-Admin-Token": token } });
    setReviews((r) => r.filter((x) => x.id !== id));
  };
  const remove = async (id) => {
    await axios.delete(`${API}/admin/reviews/${id}`, { headers: { "X-Admin-Token": token } });
    setReviews((r) => r.filter((x) => x.id !== id));
  };
  const logout = () => {
    localStorage.removeItem(LS_KEY);
    setToken("");
    setAuthed(false);
    setReviews([]);
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-4 py-24">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="w-6 h-6 text-[var(--oz-gold)]" />
          <h1 className="font-serif text-2xl text-[var(--oz-text)]">Modération des avis</h1>
        </div>
        <form onSubmit={connect} className="bg-white border border-[var(--oz-border)] rounded-2xl p-6 space-y-4">
          <label className="block text-sm text-[var(--oz-muted)]">Token administrateur</label>
          <input
            data-testid="admin-token-input"
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Collez votre token admin"
            className="w-full rounded-xl border border-[var(--oz-border)] px-4 py-3 text-sm outline-none focus:border-[var(--oz-gold)]"
          />
          {error && <p data-testid="admin-error" className="text-sm text-red-600">{error}</p>}
          <button
            data-testid="admin-connect"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[var(--oz-dark)] text-white font-semibold text-sm hover:bg-black transition-colors"
          >
            Se connecter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12 md:py-16">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-[var(--oz-gold)]" />
          <h1 className="font-serif text-2xl text-[var(--oz-text)]">Modération des avis</h1>
        </div>
        <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-1.5 text-sm text-[var(--oz-muted)] hover:text-[var(--oz-text)]">
          <LogOut className="w-4 h-4" /> Déconnexion
        </button>
      </div>

      <div className="flex gap-2 mb-6">
        {["pending", "approved"].map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              tab === t ? "bg-[var(--oz-dark)] text-white" : "bg-white border border-[var(--oz-border)] text-[var(--oz-muted)]"
            }`}
          >
            {t === "pending" ? "En attente" : "Publiés"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[var(--oz-gold)]" /></div>
      ) : reviews.length === 0 ? (
        <p data-testid="admin-empty" className="text-center text-[var(--oz-muted)] py-16">Aucun avis {tab === "pending" ? "en attente" : "publié"}.</p>
      ) : (
        <div className="space-y-4">
          {reviews.map((r) => (
            <div key={r.id} data-testid={`admin-review-${r.id}`} className="bg-white border border-[var(--oz-border)] rounded-2xl p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Stars n={r.rating} />
                  <p className="mt-2 text-sm text-[var(--oz-muted)] leading-relaxed">« {r.message} »</p>
                  <p className="mt-2 font-serif text-lg text-[var(--oz-text)]">{r.name}</p>
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  {tab === "pending" && (
                    <button
                      data-testid={`admin-approve-${r.id}`}
                      onClick={() => approve(r.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[var(--oz-gold)] text-white text-xs font-semibold hover:bg-[var(--oz-gold-hover)]"
                    >
                      <Check className="w-3.5 h-3.5" /> Publier
                    </button>
                  )}
                  <button
                    data-testid={`admin-delete-${r.id}`}
                    onClick={() => remove(r.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Supprimer
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
